import {
    VisitRequestTableRowFragment,
    VisitTableRowFragment,
} from '@/components/bulkQueue/tables/queries.gql.types';
import { SortState } from '@/components/tables/AxleTable';
import { useMemo } from 'react';

type VisitRequestSortFields = Pick<
    VisitRequestTableRowFragment,
    '__typename' | 'patient' | 'fulfillmentWindow' | 'tentativeDate'
>;

type VisitSortFields = Pick<
    VisitTableRowFragment,
    '__typename' | 'patient' | 'fulfillmentWindow' | 'tentativeDate'
>;

type TaggedItem<T, U, V> = {
    key: V;
    item: T | U;
};

type UseBulkDataOptions<T, U, V> = {
    left: T[];
    right: U[];
    getLeftSort: (item: T) => V;
    getRightSort: (item: U) => V;
    sort: (a: V, b: V) => -1 | 0 | 1;
};

export const useBulkData = <T, U, V>(options: UseBulkDataOptions<T, U, V>): (T | U)[] => {
    const { left, right, getLeftSort, getRightSort, sort } = options;

    return useMemo(() => {
        const tagged: TaggedItem<T, U, V>[] = [
            ...left.map((item) => ({ key: getLeftSort(item), item })),
            ...right.map((item) => ({ key: getRightSort(item), item })),
        ];

        return tagged.sort((a, b) => sort(a.key, b.key)).map(({ item }) => item);
    }, [left, right, getLeftSort, getRightSort, sort]);
};

const getVisitSortKey = <T extends VisitSortFields>(sortedByField: string, item: T) => {
    switch (sortedByField) {
        case 'patient.fullName':
            return item.patient.fullName;
        case 'fulfillmentWindow.startDate':
            return item.fulfillmentWindow?.startDatetime ?? null;
        default:
            return item.tentativeDate;
    }
};

const getVisitRequestSortKey = <T extends VisitRequestSortFields>(
    sortedByField: string,
    item: T,
) => {
    switch (sortedByField) {
        case 'patient.fullName':
            return item.patient.fullName;
        case 'fulfillmentWindow.startDate':
            return item.fulfillmentWindow?.startDate ?? null;
        default:
            return item.tentativeDate;
    }
};

type UseUnifyItemListOptions<
    TVisit extends VisitSortFields,
    TVisitRequest extends VisitRequestSortFields,
> = {
    visits: TVisit[];
    visitRequests: TVisitRequest[];
    sortState: SortState | null;
    pageSize: number;
};

export type UnifiedItemList<TVisit, TVisitRequest> = {
    rows: (TVisit | TVisitRequest)[];
    consumed: {
        visits: number;
        visitRequests: number;
    };
};

export const useUnifyItemList = <
    TVisit extends VisitSortFields,
    TVisitRequest extends VisitRequestSortFields,
>(
    options: UseUnifyItemListOptions<TVisit, TVisitRequest>,
): UnifiedItemList<TVisit, TVisitRequest> => {
    const { visits, visitRequests, sortState, pageSize } = options;
    const sortedByField = sortState?.sortedByField ?? '';
    const direction = sortState?.direction;

    return useMemo(() => {
        const tagged = [
            ...visits.map((item) => ({ key: getVisitSortKey(sortedByField, item), item })),
            ...visitRequests.map((item) => ({
                key: getVisitRequestSortKey(sortedByField, item),
                item,
            })),
        ];

        const sorted = tagged
            .sort((a, b) => {
                if (a.key === b.key) return 0;
                if (a.key === null) return 1;
                if (b.key === null) return -1;
                const compare = a.key < b.key ? -1 : 1;
                return (direction === 'desc' ? -compare : compare) as -1 | 1;
            })
            .map(({ item }) => item);

        const rows = sorted.slice(0, pageSize);
        const consumed = {
            visits: 0,
            visitRequests: 0,
        };

        for (const tag of rows) {
            if (tag.__typename === 'Visit') {
                consumed.visits += 1;
            } else {
                consumed.visitRequests += 1;
            }
        }

        return {
            rows,
            consumed,
        };
    }, [visits, visitRequests, sortedByField, direction, pageSize]);
};
